import { FC, useEffect } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { DepositStepHeader } from "./DepositStepHeader";

/**
 * The fixed amount of ADA every account deposits to enter the competition. Kept
 * as a named constant for now.
 *
 * TODO: source this from the ledger's `max_deposit` (streamed via the genesis
 * Init event) so the DEX can't drift from what the ledger accepts, instead of
 * duplicating the value here.
 */
export const FIXED_DEPOSIT_ADA = 10;
const FIXED_DEPOSIT_ASSET = "ada.lovelace";
const FIXED_DEPOSIT_LOVELACE = BigInt(FIXED_DEPOSIT_ADA) * 1_000_000n;

export interface DepositEntry {
  id: string;
  assetId: string;
  amount: string;
}

function createEntryId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The competition deposit is a single, fixed ADA amount. The optional argument
 * is retained for call-site compatibility but ignored -- deposits are ADA-only.
 */
export function createDepositEntry(_assetId?: string): DepositEntry {
  return {
    id: createEntryId(),
    assetId: FIXED_DEPOSIT_ASSET,
    amount: String(FIXED_DEPOSIT_ADA),
  };
}

interface AmountStepProps {
  entries: DepositEntry[];
  onEntriesChange: (entries: DepositEntry[]) => void;
  onConfirm: () => void;
  onBack?: () => void;
}

export const AmountStep: FC<AmountStepProps> = ({
  entries,
  onEntriesChange,
  onConfirm,
  onBack,
}) => {
  const { adaBalance } = useWalletObserver();

  // The deposit is fixed, so normalise the entries to the single ADA entry --
  // downstream review + submit then see exactly the competition amount.
  useEffect(() => {
    const isFixed =
      entries.length === 1 &&
      entries[0].assetId === FIXED_DEPOSIT_ASSET &&
      entries[0].amount === String(FIXED_DEPOSIT_ADA);
    if (!isFixed) onEntriesChange([createDepositEntry()]);
  }, [entries, onEntriesChange]);

  const walletLovelace = adaBalance?.amount ?? 0n;
  const hasEnough = walletLovelace >= FIXED_DEPOSIT_LOVELACE;

  return (
    <>
      <DepositStepHeader
        title="Deposit to enter"
        description={`Every account deposits a fixed ${FIXED_DEPOSIT_ADA} ADA to receive its competition balance.`}
      />

      <div className="w-full rounded-[24px] bg-sr-surface p-5">
        <div className="text-sm text-sr-neutral-400">Deposit amount</div>
        <div className="mt-1 text-[32px] font-medium tabular-nums text-white">
          {FIXED_DEPOSIT_ADA} ADA
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-sr-neutral-400">Available in wallet</span>
          <span className={hasEnough ? "text-sr-bubblegum" : "text-sr-salmon"}>
            {adaBalance?.value?.toString() ?? "0"} ADA
          </span>
        </div>
        {!hasEnough && (
          <div className="mt-2 text-sm text-sr-salmon">
            Your wallet needs at least {FIXED_DEPOSIT_ADA} ADA to deposit.
          </div>
        )}
      </div>

      <div className="flex w-full items-center gap-3">
        {onBack && (
          <button
            className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
            onClick={onBack}
            aria-label="Go back"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        <button
          className="sr-btn-primary flex-1"
          disabled={!hasEnough}
          onClick={onConfirm}
        >
          Review Deposit
        </button>
      </div>
    </>
  );
};
